/**
 * Minimal data layer: one read hook, one write hook. No cache library — the app's
 * freshness requirements are explicit (§7.4 rule 5 polling) rather than implicit,
 * so a hand-rolled `pollMs` is clearer than a stale-while-revalidate policy.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch, newIdempotencyKey, type RequestOptions } from '../api/client';
import { toProblem, type Problem } from '../api/problem';

export interface QueryState<T> {
  data: T | null;
  problem: Problem | null;
  loading: boolean;
  /** True only for the first load, so a poll never flashes a skeleton. */
  initialLoading: boolean;
  reload: () => void;
}

export interface QueryOptions extends Omit<RequestOptions, 'method' | 'body'> {
  /** Skip the request entirely (e.g. a dependent id is not known yet). */
  enabled?: boolean;
  /** Re-fetch interval in ms. §7.4 rule 5 uses 30_000 while a rental is active. */
  pollMs?: number;
}

export function useApi<T>(path: string | null, opts: QueryOptions = {}): QueryState<T> {
  const { enabled = true, pollMs, query } = opts;
  const [data, setData] = useState<T | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(Boolean(path) && enabled);
  const [nonce, setNonce] = useState(0);

  const queryKey = JSON.stringify(query ?? null);
  const active = Boolean(path) && enabled;

  useEffect(() => {
    if (!path || !active) {
      setInitialLoading(false);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();

    const run = async () => {
      setLoading(true);
      try {
        const parsedQuery = queryKey === 'null' ? undefined : (JSON.parse(queryKey) as RequestOptions['query']);
        const result = await apiFetch<T>(path, {
          ...(parsedQuery ? { query: parsedQuery } : {}),
          signal: controller.signal,
        });
        if (cancelled) return;
        setData(result);
        setProblem(null);
      } catch (e) {
        if (cancelled) return;
        setProblem(toProblem(e));
      } finally {
        if (!cancelled) {
          setLoading(false);
          setInitialLoading(false);
        }
      }
    };

    void run();
    const timer = pollMs ? window.setInterval(run, pollMs) : null;
    return () => {
      cancelled = true;
      controller.abort();
      if (timer) window.clearInterval(timer);
    };
  }, [path, queryKey, active, pollMs, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, problem, loading, initialLoading, reload };
}

export interface MutationState<TArgs, TResult> {
  run: (args: TArgs) => Promise<TResult | null>;
  data: TResult | null;
  problem: Problem | null;
  pending: boolean;
  reset: () => void;
}

export interface MutationOptions {
  method?: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /**
   * Sends an `Idempotency-Key`. Required for POSTs that charge money or create an
   * Action. The key is minted once per hook instance, so a double-submit or a
   * retry after a timeout replays rather than duplicates; `reset()` mints a new
   * one, which is what a genuinely new intent needs.
   */
  idempotent?: boolean;
}

export function useMutation<TArgs = unknown, TResult = unknown>(
  path: string | ((args: TArgs) => string),
  opts: MutationOptions = {},
): MutationState<TArgs, TResult> {
  const { method = 'POST', idempotent = false } = opts;
  const [data, setData] = useState<TResult | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [pending, setPending] = useState(false);
  const keyRef = useRef<string>(newIdempotencyKey());

  const run = useCallback(
    async (args: TArgs) => {
      setPending(true);
      setProblem(null);
      try {
        const url = typeof path === 'function' ? path(args) : path;
        const result = await apiFetch<TResult>(url, {
          method,
          body: args,
          ...(idempotent ? { idempotencyKey: keyRef.current } : {}),
        });
        setData(result);
        return result;
      } catch (e) {
        setProblem(toProblem(e));
        return null;
      } finally {
        setPending(false);
      }
    },
    [path, method, idempotent],
  );

  const reset = useCallback(() => {
    setData(null);
    setProblem(null);
    keyRef.current = newIdempotencyKey();
  }, []);

  return { run, data, problem, pending, reset };
}
